/**
 * Token Service
 * 
 * Manages authentication token storage and retrieval in localStorage. Handles
 * storing access tokens, refresh tokens, user data, and verification levels.
 * Provides helper methods for checking authentication state and clearing
 * session data. Dispatches custom events for auth state changes.
 */

import { authService } from './coreAuthService.js';
import logger from '@/shared/utils/logger.js';

export const tokenService = {
  /**
   * Stores authentication data including tokens, user data, and verification level.
   * Updates localStorage and dispatches an auth state change event.
   * @param {Object} data - Authentication data containing token, refreshToken, user, and verificationLevel
   */
  setAuthData(data) {
    try {
      // Validate input data
      if (!data || typeof data !== 'object') {
        logger.error('tokenService.setAuthData: Invalid data provided', { data });
        return;
      }

      // Clear logout flag on successful auth data update
      localStorage.removeItem('isLoggingOut');

      if (data.token || data.accessToken) {
        const token = data.token || data.accessToken;
        if (!token || typeof token !== 'string') {
          logger.error('tokenService.setAuthData: Invalid token provided', {
            token: data.token,
            accessToken: data.accessToken
          });
          return;
        }
        localStorage.setItem('authToken', token);
        localStorage.setItem('tokenExpiry', data.expiresAt || Date.now() + (24 * 60 * 60 * 1000));
        logger.debug('tokenService.setAuthData: Access token stored successfully');
      } else {
        logger.warn('tokenService.setAuthData: No token found in data', { data });
      }

      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      if (data.user) {
        localStorage.setItem('userData', JSON.stringify(data.user));
        
        // Cache user permissions if available in the user object
        if (data.user.permissions) {
          localStorage.setItem('userPermissions', JSON.stringify(data.user.permissions));
        }
      }

      if (data.verificationLevel !== undefined) {
        localStorage.setItem('verificationLevel', JSON.stringify(data.verificationLevel));
      } else if (data.user?.verificationLevel !== undefined) {
        localStorage.setItem('verificationLevel', JSON.stringify(data.user.verificationLevel));
      }

      localStorage.setItem('isAuthenticated', 'true');

      window.dispatchEvent(new CustomEvent('authStateChanged'));

    } catch (error) {
      logger.error('tokenService.setAuthData: Error storing auth data', { error, data });
    }
  },

  /**
   * Clears all authentication-related data from localStorage.
   * Removes tokens, user data, verification level, and authentication status.
   * Dispatches an auth state change event after clearing.
   */
  clearAuthData() {
    // Only clear auth data if explicitly called (e.g., logout)
    // This prevents accidental clearing during normal operations
    const itemsToRemove = [
      'authToken',
      'tokenExpiry',
      'refreshToken',
      'accessToken', // Alternative token key
      'userData',
      'verificationLevel',
      'userType',
      'walletBalance',
      'isAuthenticated',
      'twoFactorEnabled',
      'businessVerificationStatus',
      'businessVerificationTier',
      'hasSeenHelpTooltip',
      'ismartpay_tour_preferences', // Tour context storage
      'userPermissions', // Permissions cache
      'userTeams', // Teams cache
      'businessId', // Business ID cache
      'isLoggingOut' // Logout flag
    ];

    logger.debug('Clearing all authentication data and session from localStorage');

    // Remove specific known items
    itemsToRemove.forEach(item => {
      try {
        localStorage.removeItem(item);
      } catch (e) {
        logger.warn(`Failed to remove ${item} from localStorage`, { error: e });
      }
    });

    // Clear all session-related items with common prefixes
    try {
      const allKeys = Object.keys(localStorage);
      const sessionPrefixes = [
        'search_',
        'ismartpay_search_',
        'businessSettingsActiveTab',
        'react-query', // React Query cache
        'tanstack', // TanStack Query cache
        'queryClient', // Query client cache
      ];

      const keysToRemove = allKeys.filter(key => {
        // Remove items matching session prefixes
        if (sessionPrefixes.some(prefix => key.startsWith(prefix))) {
          return true;
        }
        // Remove any remaining auth/session related items (but keep language preference)
        if (key !== 'i18nextLng' && key !== 'i18next' &&
          (key.toLowerCase().includes('auth') ||
            key.toLowerCase().includes('token') ||
            key.toLowerCase().includes('session') ||
            key.toLowerCase().includes('user') ||
            key.toLowerCase().includes('business') ||
            key.toLowerCase().includes('permission') ||
            key.toLowerCase().includes('team') ||
            key.toLowerCase().includes('verification'))) {
          return true;
        }
        return false;
      });

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          logger.warn(`Failed to remove ${key} from localStorage`, { error: e });
        }
      });

      if (keysToRemove.length > 0) {
        logger.debug(`Cleared ${keysToRemove.length} additional session-related items`, { keysToRemove });
      }
    } catch (e) {
      logger.warn('Failed to clear additional session storage', { error: e });
    }

    // Clear sessionStorage completely
    try {
      sessionStorage.clear();
      logger.debug('sessionStorage cleared completely');
    } catch (e) {
      logger.warn('Failed to clear sessionStorage', { error: e });
    }

    // Clear IndexedDB if used (for React Query persistence)
    try {
      if ('indexedDB' in window) {
        // Clear React Query cache from IndexedDB if it exists
        const deleteReq = indexedDB.deleteDatabase('react-query');
        deleteReq.onsuccess = () => logger.debug('IndexedDB React Query cache cleared');
        deleteReq.onerror = () => logger.warn('Failed to clear IndexedDB cache');
      }
    } catch (e) {
      logger.warn('Failed to clear IndexedDB', { error: e });
    }

    // Dispatch event to notify other parts of the app
    try {
      window.dispatchEvent(new CustomEvent('authStateChanged'));
      logger.debug('Auth state change event dispatched');
    } catch (e) {
      logger.warn('Failed to dispatch authStateChanged event', { error: e });
    }

    // Log final state for debugging
    try {
      const remainingKeys = Object.keys(localStorage);
      logger.debug(`localStorage after cleanup: ${remainingKeys.length} items remaining (should only be language preferences)`, {
        remainingKeys: remainingKeys.length > 0 ? remainingKeys : undefined
      });
    } catch (e) {
      logger.warn('Failed to check remaining localStorage items', { error: e });
    }
  },


  /**
   * Safely logs out the user by invalidating server-side session and clearing client-side data.
   * First attempts to call the logout API to invalidate tokens on the server,
   * then always clears local data even if API calls fail.
   * @returns {Promise<Object>} Logout result with success status and optional error message
   */
  async safeLogout() {
    try {
      // Set logout flag immediately to block further API calls
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('isLoggingOut', 'true');
      }

      // Call logout API to invalidate server-side session
      try {
        const logoutResponse = await authService.logout();
        logger.debug('Logout API called successfully', { response: logoutResponse });

        // Clear all authentication and session data
        this.clearAuthData();
        logger.debug('All session data cleared');

        return { success: true, response: logoutResponse };
      } catch (apiError) {
        logger.error('Logout API call failed', { error: apiError });

        // Even if API fails, clear client-side data
        this.clearAuthData();
        logger.debug('All session data cleared (despite API error)');

        // Still return success so navigation can proceed
        return { success: true, error: apiError.message, response: null };
      }
    } catch (error) {
      logger.error('Error during logout', { error });

      // Clear data even if there's an error
      try {
        this.clearAuthData();
        logger.debug('All session data cleared (despite error)');
      } catch (clearError) {
        logger.error('Failed to clear auth data', { error: clearError });
      }

      return { success: false, error: error.message };
    }
  },

  /**
   * Checks if the user is currently authenticated by verifying token presence and validity.
   * Automatically clears expired tokens and returns false if authentication is invalid.
   * @returns {boolean} True if user is authenticated with a valid token, false otherwise
   */
  isAuthenticated() {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const token = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const tokenExpiry = localStorage.getItem('tokenExpiry');

    if (isAuthenticated === 'true' && token) {
      // If token exists, check expiry
      if (!tokenExpiry || Date.now() < parseInt(tokenExpiry)) {
        return true;
      } else {
        // Token expired, but if we have a refresh token, don't clear everything
        // The refresh token can be used to get a new access token
        if (refreshToken) {
          logger.warn('Access token expired but refresh token exists - token can be refreshed');
          // Don't clear auth data - just return false so refresh can be attempted
          return false;
        } else {
          // No refresh token, clear everything
          this.clearAuthData();
        }
      }
    }

    return false;
  },

  /**
   * Retrieves the stored access token from localStorage.
   * @returns {string|null} The access token or null if not found
   */
  getAccessToken() {
    try {
      return localStorage.getItem('authToken');
    } catch (e) {
      logger.error('Error getting access token', { error: e });
      return null;
    }
  },

  /**
   * Legacy alias for getAccessToken. Returns the stored access token.
   * @deprecated Use getAccessToken() instead
   * @returns {string|null} The access token or null if not found
   */
  getToken() {
    return this.getAccessToken();
  },

  /**
   * Retrieves the stored refresh token from localStorage.
   * @returns {string|null} The refresh token or null if not found
   */
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  },

  /**
   * Stores a refresh token in localStorage.
   * @param {string} token - The refresh token to store
   */
  setRefreshToken(token) {
    if (token) { localStorage.setItem('refreshToken', token); }
  },

  /**
   * Stores user data in localStorage.
   * @param {Object} userData - User data object to store
   */
  setUserData(userData) {
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
  },

  /**
   * Stores the user's verification level in localStorage.
   * @param {number|Object} verificationLevel - The verification level to store
   */
  setVerificationLevel(verificationLevel) {
    if (verificationLevel !== undefined) {
      localStorage.setItem('verificationLevel', JSON.stringify(verificationLevel));
    }
  },

  /**
   * Retrieves the stored verification level from localStorage.
   * @returns {Object|null} The verification level object or null if not found
   */
  getVerificationLevel() {
    try {
      const verificationLevel = localStorage.getItem('verificationLevel');
      return verificationLevel ? JSON.parse(verificationLevel) : null;
    } catch (e) {
      logger.warn('Error parsing verificationLevel from localStorage', { error: e });
      return null;
    }
  },

  /**
   * Stores the user type (individual or business_contact) in localStorage.
   * @param {string} userType - The user type to store
   */
  setUserType(userType) {
    if (userType) {
      localStorage.setItem('userType', userType);
    }
  },

  /**
   * Retrieves the user type from localStorage or userData.
   * Checks multiple possible locations and returns 'individual' as default if not found.
   * @returns {string} The user type ('individual' or 'business_contact')
   */
  getUserType() {
    const storedType = localStorage.getItem('userType');
    if (storedType) { return storedType; }

    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const typeFromUserData = userData.userType || userData.user_type || userData.usertype;
      if (typeFromUserData) { return typeFromUserData; }
    } catch (e) {
      logger.warn('Error parsing userData from localStorage', { error: e });
    }

    return 'individual';
  },

  /**
   * Stores the wallet balance in localStorage.
   * @param {number|string} balance - The wallet balance to store
   */
  setWalletBalance(balance) {
    if (balance !== undefined) {
      localStorage.setItem('walletBalance', balance.toString());
    }
  },

  /**
   * Retrieves the stored wallet balance from localStorage.
   * @returns {number} The wallet balance or 0 if not found
   */
  getWalletBalance() {
    const balance = localStorage.getItem('walletBalance');
    return balance ? parseFloat(balance) : 0;
  },

  /**
   * Retrieves the stored user data from localStorage.
   * @returns {Object|null} The user data object or null if not found
   */
  getUserData() {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      logger.error('tokenService: Error reading userData from localStorage', { error });
      return null;
    }
  },

  /**
   * Retrieves the best-fit ID to use for API headers (x-user-id).
   * If the logged in user is a business contact, prefer the business id.
   * Falls back to common user id fields for individual accounts.
   * @returns {string|null} ID string or null
   */
  getHeaderUserId() {
    try {
      const parsed = this.getUserData();
      if (!parsed) { return null; }

      const storedType = localStorage.getItem('userType');
      const userType = (storedType || parsed?.userType || parsed?.user_type || parsed?.usertype || '').toString().toLowerCase();
      const isBusinessContact = userType === 'business_contact' || userType === 'business';
      const isBusinessMember = userType === 'business_member';

      // For business members, extract business_id from team_members[0].teams.business_id
      if (isBusinessMember) {
        const businessId =
          parsed?.team_members?.[0]?.teams?.business_id ||
          parsed?.team_members?.[0]?.teams?.businessId ||
          null;

        if (businessId) {
          logger.debug('tokenService: Using business id for x-user-id header (business member)', { businessId });
          return String(businessId);
        }
        logger.warn('tokenService: Business member detected but no business id found in team_members', { parsed });
      }

      if (isBusinessContact) {
        const businessId =
          parsed?.businesses?.[0]?.id ||
          parsed?.business?.id ||
          parsed?.business_id ||
          parsed?.businessId ||
          localStorage.getItem('businessId') ||
          null;

        if (businessId) { return String(businessId); }
      }

      const userId = parsed?.id || parsed?.userId || parsed?.user_id || parsed?.customerId || parsed?.customer_id || null;
      return userId ? String(userId) : null;
    } catch (e) {
      logger.warn('tokenService.getHeaderUserId: failed to resolve id', { error: e });
      return null;
    }
  },
};

export default tokenService;
